import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../api.js";
import logoImg from "../Images/logo40.jpg";

const sparThumb = { width:"100%", aspectRatio:"1/1", objectFit:"cover", borderRadius:6, border:"1px solid #ddd", display:"block" };
const sparRemove = { position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.6)", color:"#fff", border:"none", borderRadius:"50%", width:20, height:20, fontSize:"0.85rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0 };
const sparNewBadge = { position:"absolute", bottom:4, left:4, background:"var(--brand,#bf360c)", color:"#fff", fontSize:"0.58rem", fontWeight:700, padding:"2px 5px", borderRadius:4 };
const sparAddSlot = { aspectRatio:"1/1", border:"2px dashed #ccc", borderRadius:6, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer" };

const TEXT_FIELDS = ["name","specialty","country","address","telephone","website","instagram","facebook","intro","biography","introAr","biographyAr"];

export default function EditProfile() {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const { id }   = useParams();

  const [form, setForm]           = useState(null);
  const [profile, setProfile]     = useState(null);
  const [keptUrls, setKeptUrls]   = useState([]);
  const [newFiles, setNewFiles]   = useState([]);
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState("");
  const [error, setError]         = useState("");

  const stored = JSON.parse(localStorage.getItem("spar-artist") || localStorage.getItem("spar_user") || "null");
  const token  = localStorage.getItem("spar-token") || localStorage.getItem("spar_token") || "";

  const artistId = id || stored?._id;

  useEffect(() => {
    if (!token || !artistId) { navigate("/login"); return; }
    axios.get(`${API_BASE}/getRally/${artistId}`)
      .then(r => {
        const a = r.data || {};
        setForm({
          name:        a.name        || "",
          specialty:   a.specialty   || "",
          country:     a.country     || "",
          address:     a.address     || "",
          telephone:   a.telephone   || "",
          website:     a.website     || "",
          instagram:   a.instagram   || "",
          facebook:    a.facebook    || "",
          intro:       a.intro       || "",
          biography:   a.biography   || "",
          introAr:     a.introAr     || "",
          biographyAr: a.biographyAr || "",
          _photo:      a.profileImage || "",
          _id:         a._id,
        });
        setKeptUrls(a.images || []);
      })
      .catch(() => setError("Could not load profile."));
  }, [artistId, token, navigate]);

  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const removeExisting = url => setKeptUrls(keptUrls.filter(u => u !== url));
  const removeNew      = idx => setNewFiles(newFiles.filter((_, i) => i !== idx));
  const onAddFiles     = e => {
    const slots = 4 - keptUrls.length - newFiles.length;
    if (slots <= 0) return;
    setNewFiles([...newFiles, ...Array.from(e.target.files).slice(0, slots)]);
    e.target.value = "";
  };

  const onSubmit = async e => {
    e.preventDefault(); setError(""); setMsg(""); setBusy(true);
    try {
      const data = new FormData();
      TEXT_FIELDS.forEach(k => data.append(k, form[k] || ""));
      if (profile) data.append("profileImage", profile);
      keptUrls.forEach(url => data.append("keepImages", url));
      newFiles.forEach(f => data.append("image", f));
      const res = await axios.put(`${API_BASE}/updateRally/${artistId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsg(t("editProfile.success") || "Profile updated successfully.");
      setKeptUrls(res.data.images || []);
      setNewFiles([]);
      setProfile(null);
      window.scrollTo({ top:0, behavior:"smooth" });
    } catch (err) {
      setError(err.response?.data?.error || "Update failed.");
    } finally { setBusy(false); }
  };

  if (!form) return (
    <div style={{ textAlign:"center", padding:"120px 20px", color:"var(--text-3)" }}>
      {error || t("loading")}
    </div>
  );

  return (
    <div style={{ maxWidth:680, margin:"100px auto 60px", padding:"0 20px" }}>
      <div className="spar-card" style={{ padding:"40px 36px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:28 }}>
          <img src={logoImg} alt="SPAR" style={{ width:52, height:52, borderRadius:10 }} />
          <div>
            <h1 className="spar-page-title" style={{ marginBottom:2 }}>{t("editProfile.title") || "Edit Profile"}</h1>
            <p style={{ color:"var(--text-3)", fontSize:"0.875rem", margin:0 }}>{t("editProfile.subtitle") || "Update your artist information"}</p>
          </div>
        </div>

        {msg   && <div className="spar-alert" style={{ background:"#e6f9ee", borderColor:"#22c55e", color:"#166534", marginBottom:16 }}>{msg}</div>}
        {error && <div className="spar-alert spar-alert-error">{error}</div>}

        <form onSubmit={onSubmit} noValidate>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.name")} *</label>
            <input type="text" name="name" value={form.name} className="spar-input" onChange={onChange} required />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.specialty")} *</label>
            <input type="text" name="specialty" value={form.specialty} className="spar-input" onChange={onChange} required />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.country")}</label>
            <input type="text" name="country" value={form.country} className="spar-input" onChange={onChange} />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.address")}</label>
            <input type="text" name="address" value={form.address} className="spar-input" onChange={onChange} />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.telephone")}</label>
            <input type="tel" name="telephone" value={form.telephone} className="spar-input" onChange={onChange} />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.website")}</label>
            <input type="url" name="website" value={form.website} className="spar-input" onChange={onChange} placeholder="https://" />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.instagram")}</label>
            <input type="text" name="instagram" value={form.instagram} className="spar-input" onChange={onChange} />
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.facebook")}</label>
            <input type="text" name="facebook" value={form.facebook} className="spar-input" onChange={onChange} />
          </div>

          <div className="spar-form-group">
            <label className="spar-label">{t("signup.intro")} *</label>
            <textarea name="intro" rows={4} maxLength={500} value={form.intro} className="spar-textarea" onChange={onChange} required />
            <p style={{ fontSize:"0.75rem", color:"var(--text-3)", marginTop:4 }}>{form.intro.length}/500</p>
          </div>
          <div className="spar-form-group">
            <label className="spar-label">{t("signup.biography")} *</label>
            <textarea name="biography" rows={7} maxLength={1500} value={form.biography} className="spar-textarea" onChange={onChange} required />
            <p style={{ fontSize:"0.75rem", color:"var(--text-3)", marginTop:4 }}>{form.biography.length}/1500</p>
          </div>

          <div className="spar-form-group" style={{ background:"rgba(191,54,12,0.05)", border:"1px solid rgba(191,54,12,0.15)", borderRadius:8, padding:"18px 16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
              <span>🌐</span>
              <span style={{ fontWeight:700, fontSize:"0.9rem" }}>{t("signup.arabicFields") || "Arabic Fields"}</span>
              <span style={{ fontSize:"0.78rem", color:"var(--text-3)" }}>{t("signup.arabicOptional") || "(optional)"}</span>
            </div>
            <div style={{ marginBottom:12 }}>
              <label className="spar-label" style={{ direction:"rtl", textAlign:"right", display:"block" }}>{t("signup.introAr") || "نبذة قصيرة بالعربية"}</label>
              <textarea name="introAr" rows={3} maxLength={500} value={form.introAr} className="spar-textarea" dir="rtl" style={{ fontFamily:"Cairo, Tajawal, sans-serif" }} onChange={onChange} />
            </div>
            <div>
              <label className="spar-label" style={{ direction:"rtl", textAlign:"right", display:"block" }}>{t("signup.biographyAr") || "السيرة الذاتية بالعربية"}</label>
              <textarea name="biographyAr" rows={6} maxLength={1500} value={form.biographyAr} className="spar-textarea" dir="rtl" style={{ fontFamily:"Cairo, Tajawal, sans-serif" }} onChange={onChange} />
            </div>
          </div>

          <div className="spar-form-group">
            <label className="spar-label">{t("signup.profileImage")}</label>
            {form._photo && !profile && (
              <img src={form._photo} alt="current" style={{ width:72, height:72, borderRadius:"50%", objectFit:"cover", marginBottom:8, display:"block" }} />
            )}
            <input type="file" accept="image/*" className="spar-input" onChange={e => setProfile(e.target.files[0]||null)} />
            {profile && <img src={URL.createObjectURL(profile)} alt="new" style={{ width:72, height:72, borderRadius:"50%", objectFit:"cover", marginTop:8, display:"block" }} />}
          </div>

          <div className="spar-form-group">
            <label className="spar-label" style={{ display:"flex", justifyContent:"space-between" }}>
              <span>{t("signup.artworks")}</span>
              <span style={{ fontWeight:400, color:"var(--text-3)", fontSize:"0.8rem" }}>
                {keptUrls.length + newFiles.length}/4
              </span>
            </label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginTop:8 }}>
              {keptUrls.map((url, i) => (
                <div key={`k-${i}`} style={{ position:"relative" }}>
                  <img src={url} alt={`work ${i+1}`} style={sparThumb} />
                  <button type="button" onClick={() => removeExisting(url)} style={sparRemove}>×</button>
                </div>
              ))}
              {newFiles.map((f, i) => (
                <div key={`n-${i}`} style={{ position:"relative" }}>
                  <img src={URL.createObjectURL(f)} alt={`new ${i+1}`} style={{ ...sparThumb, outline:"2px solid var(--brand,#bf360c)" }} />
                  <button type="button" onClick={() => removeNew(i)} style={sparRemove}>×</button>
                  <span style={sparNewBadge}>new</span>
                </div>
              ))}
              {keptUrls.length + newFiles.length < 4 && (
                <label style={sparAddSlot}>
                  <span style={{ fontSize:"1.5rem", color:"var(--text-3)" }}>+</span>
                  <span style={{ fontSize:"0.7rem", color:"var(--text-3)", marginTop:2 }}>Add</span>
                  <input type="file" accept="image/*" multiple style={{ display:"none" }} onChange={onAddFiles} />
                </label>
              )}
            </div>
          </div>

          <button type="submit" className="spar-btn spar-btn-primary" disabled={busy} style={{ width:"100%", justifyContent:"center", height:48, marginTop:8 }}>
            {busy ? (<><span className="spar-spinner" />{t("editProfile.saving") || "Saving..."}</>) : (t("editProfile.saveChanges") || "Save Changes")}
          </button>

          <a href={`/ArtistProfile/${artistId}`} className="spar-btn" style={{ display:"flex", justifyContent:"center", marginTop:12, textDecoration:"none" }}>
            {t("profile.viewProfile") || "View my profile"}
          </a>
        </form>
      </div>
    </div>
  );
}
